import { useMemo, useState } from 'react'
import { Helmet } from 'react-helmet-async'

import CssBaseline from '@mui/material/CssBaseline'
import { ThemeProvider } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ToastContainer } from 'components'
import { AnalyticsProvider, DeviceIdentificationProvider, RecaptchaProvider } from 'contexts'
import { setCustomTheme } from 'oneui'
import Router from 'router'
import getDefaultTheme from 'styles/theme'

import i18n from './utils/i18n'

const queryClient = new QueryClient()

function App() {
  const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)')
  const theme = useMemo(() => getDefaultTheme(prefersDarkMode), [prefersDarkMode])
  const themeBackground = theme.palette.background
  const themeColor = prefersDarkMode ? themeBackground.default : themeBackground.paper
  const [languageCode, setLanguageCode] = useState<string>('en')
  const [locale, setLocale] = useState<string>('en-US')

  i18n.on('languageChanged', (lng) => {
    setLanguageCode(lng.split('-')[0])
    setLocale(lng)
  })

  setCustomTheme(theme)

  const analyticsEnabled = process.env.REACT_APP_ANALYTICS_ENABLED === 'true'
  const recaptchaEnabled = process.env.REACT_APP_RECAPTCHA_ENABLED === 'true'

  const getLogoPath = (locale: string) => {
    switch (locale) {
      case 'fr-FR':
        return `/assets/images/header/logo-fr.svg`
      case 'ja-JP':
        return `/assets/images/header/logo-ja.svg`
      case 'zh-HK':
        return `/assets/images/header/logo-zh-HK.svg`
      case 'zh-TW':
        return `/assets/images/header/logo-zh-TW.svg`
      case 'zh-CN':
        return `/assets/images/header/logo-zh.svg`
      default:
        return `/assets/images/header/logo.svg`
    }
  }

  const metaTitle = {
    en: 'Samsung account',
    fr: 'Compte Samsung',
    ja: 'Samsungアカウント',
    ko: '삼성계정'
  }

  const metaDescription = {
    en: 'One account. Any device. Just for you. Sign in to get started.',
    de: 'Ein Konto für all deine Geräte. Jetzt anmelden, um zu beginnen.',
    es: 'Una cuenta. Cualquier dispositivo. Solo para ti. Inicia sesión para comenzar.',
    fr: "Un compte. N'importe quel appareil. Juste pour vous. Connectez-vous pour démarrer.",
    ja: '1つのアカウントで、あらゆるデバイスをあなた専用にしましょう。サインインして開始。',
    ko: '하나의 계정으로, 모든 기기에서, 나만을 위한 서비스를! 삼성 계정에 로그인하세요.'
  }

  const seo = (
    <>
      <meta name='theme-color' content={themeColor} />
      <meta name='title' content={metaTitle[languageCode] || metaTitle['en']} />
      <meta name='description' content={metaDescription[languageCode] || metaDescription['en']} />
      <meta property='og:type' content='website' />
      <meta property='og:url' content={process.env.REACT_APP_HOST} />
      <meta property='og:title' content={metaTitle[languageCode] || metaTitle['en']} />
      <meta property='og:image' content={process.env.REACT_APP_HOST + getLogoPath(locale)} />
      <meta property='og:description' content={metaDescription[languageCode] || metaDescription['en']} />
      <meta property='og:site_name' content={metaTitle[languageCode] || metaTitle['en']} />
    </>
  )

  let body
  if (analyticsEnabled && recaptchaEnabled) {
    body = (
      <AnalyticsProvider>
        <RecaptchaProvider>
          <Helmet>
            <html lang={languageCode}></html>
            {seo}
          </Helmet>
          <Router />
        </RecaptchaProvider>
      </AnalyticsProvider>
    )
  } else if (analyticsEnabled && !recaptchaEnabled) {
    body = (
      <AnalyticsProvider>
        <Helmet>
          <html lang={languageCode}></html>
          {seo}
        </Helmet>
        <Router />
      </AnalyticsProvider>
    )
  } else if (!analyticsEnabled && recaptchaEnabled) {
    body = (
      <RecaptchaProvider>
        <Helmet>
          <html lang={languageCode}></html>
          {seo}
        </Helmet>
        <Router />
      </RecaptchaProvider>
    )
  } else {
    body = (
      <>
        <Helmet>
          <html lang={languageCode}></html>
          {seo}
        </Helmet>
        <Router />
      </>
    )
  }

  return (
    <ThemeProvider theme={theme}>
      <QueryClientProvider client={queryClient}>
        <CssBaseline />
        <DeviceIdentificationProvider>{body}</DeviceIdentificationProvider>
        <ToastContainer />
      </QueryClientProvider>
    </ThemeProvider>
  )
}

export default App
